import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Spinner } from "react-bootstrap";
import { FaChevronLeft, FaChevronRight, FaSyncAlt } from "react-icons/fa";
import InspectionCard from "../components/InspectionCard.jsx";
import { listInspectionJobs } from "../services/inspectionApi.js";
import { listQueuedFinishes } from "../services/outbox.js";
import { cacheJobList, getCachedJobList, listUploads } from "../storage/inspectionStore.js";
import { addInputDays, formatDate, todayInput } from "../../utils/date.js";

function InspectionHomePage({ currentUser }) {
  const [date, setDate] = useState(todayInput());
  // What this screen last believed "today" to be (see the visibility effect below).
  const knownToday = useRef(todayInput());
  const [jobs, setJobs] = useState([]);
  const [queued, setQueued] = useState([]);
  const [finishes, setFinishes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cached, setCached] = useState(false);
  const [error, setError] = useState("");

  const loadQueued = useCallback(() => {
    listUploads(currentUser.id).then(setQueued).catch(() => setQueued([]));
    listQueuedFinishes(currentUser.id).then(setFinishes).catch(() => setFinishes([]));
  }, [currentUser.id]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await listInspectionJobs(date);
      setJobs(result.jobs);
      setCached(false);
      await cacheJobList(currentUser.id, date, result.jobs);
    } catch (requestError) {
      const saved = await getCachedJobList(currentUser.id, date).catch(() => null);
      if (saved) {
        setJobs(saved);
        setCached(true);
      } else {
        setJobs([]);
        setError(requestError.response?.data?.error || "Unable to load inspections.");
      }
    } finally {
      setLoading(false);
    }
  }, [currentUser.id, date]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadQueued();
    window.addEventListener("inspection-queue-changed", loadQueued);
    return () => window.removeEventListener("inspection-queue-changed", loadQueued);
  }, [loadQueued]);

  // An installed app is never closed, it is put in a pocket — so "Today" stayed
  // whatever day it was when the phone was last unlocked, and the next morning's
  // list showed yesterday's jobs under a heading that said Today.
  //
  // Only a view that WAS today rolls forward. A day the inspector deliberately
  // navigated to stays where they left it, which is why this compares against the
  // remembered "today" rather than against today-minus-one.
  useEffect(() => {
    function catchUp() {
      if (document.visibilityState !== "visible") return;
      const now = todayInput();
      if (now === knownToday.current) return;
      setDate((current) => (current === knownToday.current ? now : current));
      knownToday.current = now;
    }
    document.addEventListener("visibilitychange", catchUp);
    return () => document.removeEventListener("visibilitychange", catchUp);
  }, []);

  const pendingByInspection = useMemo(() => queued.reduce((map, row) => {
    map[row.inspectionId] = (map[row.inspectionId] || 0) + 1;
    return map;
  }, {}), [queued]);

  // A job finished in a dead spot is finished as far as the inspector is concerned,
  // even though the server list still says otherwise — show it that way, or they
  // will open it and finish it a second time.
  const finishedOnDevice = useMemo(
    () => new Set(finishes.map((row) => Number(row.inspectionId))),
    [finishes]
  );

  return (
    <div className="inspection-page mx-auto">
      <div className="d-flex align-items-center justify-content-between gap-2 mb-3">
        <Button variant="outline-secondary" onClick={() => setDate((d) => addInputDays(d, -1))} aria-label="Previous day">
          <FaChevronLeft />
        </Button>
        <div className="text-center">
          <h1 className="h4 mb-0">{date === todayInput() ? "Today" : formatDate(date, "long")}</h1>
          {date === todayInput() ? <div className="small text-body-secondary">{formatDate(date, "long")}</div> : null}
        </div>
        <Button variant="outline-secondary" onClick={() => setDate((d) => addInputDays(d, 1))} aria-label="Next day">
          <FaChevronRight />
        </Button>
      </div>

      {cached ? <Alert variant="warning">Showing the last saved list while offline.</Alert> : null}
      {error ? <Alert variant="danger">{error} <Button variant="link" className="p-0 ms-1" onClick={load}><FaSyncAlt /> Retry</Button></Alert> : null}

      {loading ? (
        <div className="text-center text-body-secondary py-5"><Spinner size="sm" /> Loading inspections…</div>
      ) : jobs.length === 0 && !error ? (
        <div className="text-center text-body-secondary bg-body rounded border p-5">
          No inspections assigned for this day.
        </div>
      ) : (
        <div className="row g-3">
          {jobs.map((job) => (
            <div key={job.inspection_id} className="col-12 col-lg-6">
              <InspectionCard
                job={job}
                pending={pendingByInspection[job.inspection_id] || 0}
                finishPending={finishedOnDevice.has(Number(job.inspection_id))}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default InspectionHomePage;
